/**
 * @file src/seo/routeMeta.js
 * @description Centralized metadata table & dynamic route resolution for VelaiVendum.
 * Maps static paths and dynamic patterns to SEO configurations.
 * @module routeMeta
 */

import { getTitle, getDescription, getKeywords, getCanonical, getSchema, DEFAULT_OG_IMAGE } from './seoHelpers';

/**
 * Static route SEO metadata configuration
 */
export const routeMeta = {
  '/': {
    pageType: 'home',
    type: 'website',
    image: DEFAULT_OG_IMAGE
  },
  '/login': {
    pageType: 'login',
    type: 'website',
    image: DEFAULT_OG_IMAGE
  },
  '/post-job': {
    pageType: 'employer_register',
    type: 'website',
    image: DEFAULT_OG_IMAGE
  },
  '/jobseeker': {
    pageType: 'jobseeker_register',
    type: 'website',
    image: DEFAULT_OG_IMAGE
  },
  '/employer': {
    pageType: 'employer_dashboard',
    type: 'website',
    image: DEFAULT_OG_IMAGE
  },
  '/volunteer': {
    pageType: 'about',
    type: 'website',
    image: DEFAULT_OG_IMAGE
  },
  '/mentor': {
    pageType: 'about',
    type: 'website',
    image: DEFAULT_OG_IMAGE
  },
  '/termsandcondition': {
    pageType: 'terms',
    type: 'website',
    image: DEFAULT_OG_IMAGE
  },
  '/privacypolicy': {
    pageType: 'privacy',
    type: 'website',
    image: DEFAULT_OG_IMAGE
  },
  '/purchasepolicy': {
    pageType: 'purchase',
    type: 'website',
    image: DEFAULT_OG_IMAGE
  },
  '/refundpolicy': {
    pageType: 'refund',
    type: 'website',
    image: DEFAULT_OG_IMAGE
  },
  '/deliverypolicy': {
    pageType: 'delivery',
    type: 'website',
    image: DEFAULT_OG_IMAGE
  },
  '/pricingpolicy': {
    pageType: 'pricing',
    type: 'website',
    image: DEFAULT_OG_IMAGE
  },
  '/companies': {
    pageType: 'companies',
    type: 'website',
    image: DEFAULT_OG_IMAGE
  },
  '/searchpage': {
    pageType: 'jobs_list',
    type: 'website',
    image: DEFAULT_OG_IMAGE
  }
};

/**
 * Resolves SEO metadata for a given pathname and optional page data / props.
 * Supports exact route matching, dynamic regex matching, and custom data overrides.
 *
 * @param {string} pathname - Current window/router pathname
 * @param {Object} [customData={}] - Dynamic props passed directly to SEO component
 * @returns {Object} Evaluated SEO metadata object with title, description, keywords, canonical, and schema
 */
export function resolveRouteSEO(pathname = '/', customData = {}) {
  let matchedConfig = routeMeta[pathname];
  let pageType = matchedConfig ? matchedConfig.pageType : 'home';

  // Dynamic route pattern resolution
  if (!matchedConfig) {
    if (pathname.includes('/job/') || pathname.includes('/jobseeker/job/')) {
      pageType = 'job_detail';
    } else if (pathname.includes('/company/')) {
      pageType = 'company_detail';
    } else if (pathname.includes('/searchpage')) {
      pageType = 'jobs_list';
    }
  }

  const mergedData = { ...customData };

  const canonicalUrl = getCanonical(pathname, undefined, mergedData.queryParams);
  const evaluatedTitle = mergedData.title || getTitle(pageType, mergedData);
  const evaluatedDescription = mergedData.description || getDescription(pageType, mergedData);
  const evaluatedKeywords = mergedData.keywords || getKeywords(pageType, mergedData);
  const evaluatedSchemas = getSchema(pageType, mergedData, canonicalUrl);

  return {
    pageType,
    title: evaluatedTitle,
    description: evaluatedDescription,
    keywords: evaluatedKeywords,
    canonical: canonicalUrl,
    image: mergedData.image || DEFAULT_OG_IMAGE,
    type: pageType === 'job_detail' ? 'article' : 'website',
    schemas: evaluatedSchemas
  };
}
